import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Check, Shield, Rocket, Crown, ArrowRight, Sparkles, Cloud, Monitor } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const defaultContent = {
  section_label: "Soluciones",
  title: "Elige tu Nivel",
  subtitle: "Planes flexibles diseñados para crecer con tu negocio. Sin sorpresas, sin costos ocultos.",
  plans: [
    {
      id: "basic",
      name: "Basic",
      tagline: "El comienzo inteligente",
      description: "Ideal para pequenas empresas que inician su transformacion digital.",
      icon: Shield,
      color: "#3b82f6",
      features: [
        "Sistema operativo basico",
        "Actualizaciones mensuales",
        "Soporte por email",
        "1 Usuario administrador",
        "Almacenamiento 5GB",
      ],
      featured: false,
    },
    {
      id: "pro",
      name: "Pro",
      tagline: "Potencia sin limites",
      description: "Para empresas en crecimiento que necesitan mas potencia y soporte.",
      icon: Rocket,
      color: "#06b6d4",
      features: [
        "Sistema operativo completo",
        "Seguridad avanzada",
        "Soporte prioritario 24/7",
        "5 Usuarios administradores",
        "Almacenamiento 50GB",
        "Reportes avanzados",
        "Integraciones API",
      ],
      featured: true,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      tagline: "Control absoluto",
      description: "Solucion completa para grandes empresas con necesidades especificas.",
      icon: Crown,
      color: "#f59e0b",
      features: [
        "Sistema operativo personalizado",
        "Licencias ilimitadas",
        "Soporte 24/7 dedicado",
        "Usuarios ilimitados",
        "Almacenamiento ilimitado",
        "Capacitacion presencial",
        "Desarrollo a medida",
        "SLA garantizado",
      ],
      featured: false,
    },
  ],
};

const PlanCard = ({ plan, index, isInView }) => {
  const { t } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const Icon = plan.icon;

  return (
    <motion.div
      data-testid={`pricing-card-${plan.id}`}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15, type: "spring", damping: 20 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative group ${plan.featured ? "md:-mt-6 md:mb-6 z-10" : "z-0"}`}
    >
      {/* Outer glow for featured */}
      {plan.featured && (
        <motion.div
          className="absolute -inset-[1px] rounded-2xl opacity-60"
          style={{
            background: `linear-gradient(135deg, ${plan.color}, #3b82f6, ${plan.color})`,
            backgroundSize: "200% 200%",
          }}
          animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      )}

      {/* Animated border for non-featured on hover */}
      <motion.div
        className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `linear-gradient(135deg, ${plan.color}60, transparent, ${plan.color}60)`,
        }}
      />

      <div
        className={`relative rounded-2xl overflow-hidden h-full ${
          plan.featured ? "bg-[#0a1628]" : "bg-[#080e1c]"
        }`}
      >
        {/* Animated gradient overlay for featured */}
        {plan.featured && (
          <motion.div
            className="absolute inset-0 opacity-10"
            style={{
              background: `radial-gradient(circle at 50% 0%, ${plan.color} 0%, transparent 60%)`,
            }}
            animate={{ opacity: [0.05, 0.15, 0.05] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
        )}

        {/* Particle dots on hover */}
        <motion.div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full"
              style={{
                backgroundColor: plan.color,
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
              }}
              animate={
                isHovered
                  ? {
                      y: [0, -20, 0],
                      opacity: [0, 0.8, 0],
                    }
                  : {}
              }
              transition={{
                duration: 2,
                delay: i * 0.2,
                repeat: Infinity,
              }}
            />
          ))}
        </motion.div>

        {/* Featured badge */}
        {plan.featured && (
          <motion.div
            className="absolute top-0 left-0 right-0 flex justify-center"
            initial={{ y: -30, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.6, type: "spring" }}
          >
            <div
              className="px-6 py-1.5 text-[10px] font-black uppercase tracking-[0.3em] text-white rounded-b-lg flex items-center gap-2"
              style={{ backgroundColor: plan.color }}
            >
              <Sparkles size={12} />
              {t("pricing_popular")}
              <Sparkles size={12} />
            </div>
          </motion.div>
        )}

        <div className="relative z-10 p-8 pt-10 flex flex-col h-full">
          {/* Icon + Name */}
          <div className="flex items-center gap-4 mb-5">
            <motion.div
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: `${plan.color}15`,
                border: `1px solid ${plan.color}30`,
              }}
              whileHover={{ rotate: 10, scale: 1.1 }}
              transition={{ type: "spring" }}
            >
              <Icon size={26} style={{ color: plan.color }} />
            </motion.div>
            <div>
              <h3 className="text-2xl font-black text-white tracking-tight">
                {plan.name}
              </h3>
              <p
                className="text-xs font-bold uppercase tracking-[0.2em]"
                style={{ color: plan.color }}
              >
                {plan.tagline}
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-[#94a3b8] text-sm leading-relaxed mb-7">
            {plan.description}
          </p>

          {/* Divider */}
          <div
            className="h-px mb-6"
            style={{
              background: `linear-gradient(90deg, transparent, ${plan.color}30, transparent)`,
            }}
          />

          {/* Features */}
          <ul className="space-y-3 mb-8 flex-1">
            {plan.features.map((feature, i) => (
              <motion.li
                key={i}
                className="flex items-start gap-3"
                initial={{ opacity: 0, x: -15 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.1 + i * 0.05 }}
              >
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    backgroundColor: `${plan.color}15`,
                  }}
                >
                  <Check size={12} style={{ color: plan.color }} />
                </div>
                <span className="text-[#c8d0dc] text-sm">{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* CTA Button */}
          <motion.button
            data-testid={`select-plan-${plan.id}`}
            onClick={() =>
              document
                .querySelector("#contacto")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className={`w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider relative overflow-hidden flex items-center justify-center gap-2 transition-all duration-300 ${
              plan.featured
                ? "text-white"
                : "text-white/90 border"
            }`}
            style={{
              backgroundColor: plan.featured ? plan.color : "transparent",
              borderColor: plan.featured ? "transparent" : `${plan.color}40`,
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: `0 0 30px ${plan.color}40`,
              borderColor: plan.color,
            }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Sweep effect */}
            <motion.span
              className="absolute inset-0 bg-white/10"
              initial={{ x: "-100%", skewX: "-15deg" }}
              whileHover={{ x: "150%" }}
              transition={{ duration: 0.6 }}
            />
            <span className="relative">{t("pricing_cta")}</span>
            <ArrowRight size={16} className="relative" />
          </motion.button>
        </div>

        {/* Bottom accent line */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{
            background: `linear-gradient(90deg, transparent, ${plan.color}, transparent)`,
          }}
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: isHovered ? 1 : 0, scaleX: isHovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />
      </div>
    </motion.div>
  );
};

const iconMap = {
  basic: Shield,
  pro: Rocket,
  enterprise: Crown,
};

const colorMap = {
  basic: "#3b82f6",
  pro: "#06b6d4",
  enterprise: "#f59e0b",
};

const taglineMap = {
  basic: "El comienzo inteligente",
  pro: "Potencia sin limites",
  enterprise: "Control absoluto",
};

const PricingSection = ({ content }) => {
  const rawData = { ...defaultContent, ...content };
  // Merge CMS plans with default visual data
  const data = {
    ...rawData,
    plans: (rawData.plans || defaultContent.plans).map((plan) => ({
      ...plan,
      icon: iconMap[plan.id] || Shield,
      color: colorMap[plan.id] || "#3b82f6",
      tagline: taglineMap[plan.id] || "",
    })),
  };
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section
      id="precios"
      ref={sectionRef}
      data-testid="pricing-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#050a15]" />

      {/* Large glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full"
        style={{
          background:
            "radial-gradient(ellipse, rgba(6, 182, 212, 0.06) 0%, rgba(59, 130, 246, 0.03) 40%, transparent 70%)",
        }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.span
            className="inline-block px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.4em] text-[#06b6d4] border border-[#06b6d4]/20 bg-[#06b6d4]/5 mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
          >
            {t("pricing_badge")}
          </motion.span>

          <motion.h2
            data-testid="pricing-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-5 tracking-tight"
          >
            {t("pricing_title").split(" ")[0]}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] via-[#06b6d4] to-[#f59e0b]">
              {t("pricing_title").split(" ").slice(1).join(" ")}
            </span>
          </motion.h2>

          <motion.p className="text-[#94a3b8] text-base sm:text-lg max-w-xl mx-auto">
            {t("pricing_subtitle")}
          </motion.p>

          <motion.p className="text-[#3b82f6]/40 text-sm font-semibold tracking-widest uppercase mt-4">
            Global Metafora APP
          </motion.p>

          {/* Cloud / Local versions */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mt-6"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            data-testid="pricing-versions"
          >
            <div className="flex items-center gap-2">
              <Cloud size={15} className="text-[#06b6d4]" />
              <span className="text-xs text-[#94a3b8]">
                <span className="text-[#06b6d4] font-semibold">{t("version_cloud")}</span> — {t("version_cloud_desc").replace("(", "").replace(")", "").split("—")[1]?.trim() || "suscripcion mensual, WhatsApp, dominio, rastreo publico"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor size={15} className="text-[#10b981]" />
              <span className="text-xs text-[#94a3b8]">
                <span className="text-[#10b981] font-semibold">{t("version_local")}</span> — {t("version_local_desc").replace("(", "").replace(")", "").split("—")[1]?.trim() || "pago unico, instalado en su PC, sin costos recurrentes"}
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto items-stretch">
          {data.plans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Bottom text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1.2 }}
          className="text-center mt-14 space-y-3"
        >
          <p className="text-[#64748b] text-sm">
            {t("pricing_all_include")}{" "}
            <span className="text-[#3b82f6] font-semibold">
              Global Metafora APP
            </span>
          </p>
          <motion.button
            data-testid="contact-enterprise"
            onClick={() =>
              document
                .querySelector("#contacto")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="text-[#06b6d4] text-sm font-semibold hover:text-white transition-colors inline-flex items-center gap-2"
            whileHover={{ x: 5 }}
          >
            {t("pricing_different")}
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight size={14} />
            </motion.span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
